import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { authAPI } from '../utils/api';
import BRAND from '../brand';
import { Mail, Lock, MessageCircle, Zap, Shield, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function Login() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const setUser = useAuthStore((state) => state.setUser);
  const setToken = useAuthStore((state) => state.setToken);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setLoading(true);

    try {
      const response = await authAPI.login(formData);
      if (response.data && response.data.success && response.data.data && response.data.data.token && response.data.data.user) {
        const { token, user } = response.data.data;
        setToken(token);
        setUser(user);
        if (user.role === 'admin') {
          navigate('/admin');
        } else {
          navigate('/dashboard');
        }
      } else {
        // Show backend error if available, else generic error
        const backendMsg = response.data?.error || response.data?.message || 'Login failed: No token/user returned.';
        setErrors({ submit: backendMsg });
      }
    } catch (error) {
      // Show exact backend error message if available
      const backendMsg = error.response?.data?.error || error.response?.data?.message;
      setErrors({ submit: backendMsg || 'Login failed' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0E14] relative overflow-hidden" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Animated Background Elements */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-20 left-20 w-96 h-96 bg-[#128C7E]/10 rounded-full blur-3xl opacity-20"></div>
        <div className="absolute bottom-40 right-20 w-96 h-96 bg-[#8B5CF6]/10 rounded-full blur-3xl opacity-20"></div>
      </div>

      {/* Language Switcher */}
      <div className="absolute top-6 right-6 z-50 flex gap-2 bg-[#1A1F2E]/80 border border-[#2D3748] rounded-lg p-1.5 backdrop-blur-xl">
        <button
          onClick={() => i18n.changeLanguage('en')}
          className={`px-4 py-2 rounded-md font-semibold text-sm transition-all ${
            i18n.language === 'en'
              ? 'bg-[#128C7E] text-white shadow-lg shadow-[#128C7E]/40'
              : 'text-[#A0AEC0] hover:text-white'
          }`}
        >
          ENG
        </button>
        <button
          onClick={() => i18n.changeLanguage('ar')}
          className={`px-4 py-2 rounded-md font-semibold text-sm transition-all ${
            i18n.language === 'ar'
              ? 'bg-[#128C7E] text-white shadow-lg shadow-[#128C7E]/40'
              : 'text-[#A0AEC0] hover:text-white'
          }`}
        >
          AR
        </button>
      </div>

      <div className="relative z-10 min-h-screen grid grid-cols-1 lg:grid-cols-2">
        
        {/* Form Section */}
        <div className={`flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12 ${isRTL ? 'order-2' : 'order-1'}`}>
          <div className="w-full max-w-md">
            <div className="mb-8">
              <Link to="/" className={`inline-block text-3xl font-black bg-gradient-to-r from-[#128C7E] to-[#0DA699] bg-clip-text text-transparent mb-6`}>
                {BRAND[i18n.language || 'en']}
              </Link>
              <h1 className="text-4xl md:text-5xl font-black text-white mb-3">
                {t('Welcome back')} 👋
              </h1>
              <p className="text-[#A0AEC0] text-lg">
                {t('Sign in to access your dashboard')}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {errors.submit && (
                <div className="p-4 bg-[#EF4444]/15 border border-[#EF4444]/50 rounded-xl flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#EF4444] flex-shrink-0 mt-0.5"></div>
                  <p className="text-[#EF4444] text-sm font-medium">{errors.submit}</p>
                </div>
              )}

              {/* Email Field */}
              <div>
                <label className="block text-sm font-semibold text-white mb-2">{t('Email Address')}</label>
                <div className="relative">
                  <Mail className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-[#A0AEC0] ${isRTL ? 'right-4' : 'left-4'}`} />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t('you@example.com')}
                    required
                    className={`w-full bg-[#1A1F2E]/80 border border-[#2D3748] rounded-xl py-3 text-white placeholder-[#718096] focus:outline-none focus:border-[#128C7E] focus:shadow-lg focus:shadow-[#128C7E]/20 transition-all ${isRTL ? 'pr-12 pl-4' : 'pl-12 pr-4'}`}
                  />
                </div>
              </div>

              {/* Password Field */}
              <div>
                <label className="block text-sm font-semibold text-white mb-2">{t('Password')}</label>
                <div className="relative">
                  <Lock className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-[#A0AEC0] ${isRTL ? 'right-4' : 'left-4'}`} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    required
                    className={`w-full bg-[#1A1F2E]/80 border border-[#2D3748] rounded-xl py-3 text-white placeholder-[#718096] focus:outline-none focus:border-[#128C7E] focus:shadow-lg focus:shadow-[#128C7E]/20 transition-all ${isRTL ? 'pr-12 pl-12' : 'pl-12 pr-12'}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute top-1/2 -translate-y-1/2 text-[#A0AEC0] hover:text-white transition-colors ${isRTL ? 'left-4' : 'right-4'}`}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* Remember & Forgot */}
              <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                <label className={`flex items-center gap-2 cursor-pointer ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <input type="checkbox" className="w-4 h-4 rounded accent-[#128C7E]" />
                  <span className="text-sm text-[#A0AEC0] hover:text-white transition-colors">{t('Remember me')}</span>
                </label>
                <Link to="#" className="text-sm text-[#128C7E] hover:text-[#0DA699] transition-colors font-medium">
                  {t('Forgot password?')}
                </Link>
              </div>

              {/* Submit Button */}
              <button 
                type="submit" 
                disabled={loading}
                className="w-full bg-gradient-to-r from-[#128C7E] to-[#0DA699] hover:shadow-xl hover:shadow-[#128C7E]/40 text-white font-bold py-3.5 rounded-xl transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2 active:scale-95"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    {t('Signing In...')}
                  </>
                ) : (
                  <>
                    {t('Sign In')}
                    <ArrowRight size={20} />
                  </>
                )}
              </button>
            </form>

            {/* Sign Up Link */}
            {/* <p className={`mt-6 text-[#A0AEC0] ${isRTL ? 'text-right' : 'text-left'}`}>
              {t("Don't have an account?")} <Link to="/register" className="text-[#128C7E] hover:text-[#0DA699] font-semibold transition-colors">{t('Create one')}</Link>
            </p> */}

          </div>
        </div>

        {/* Features Section */}
        <div className={`hidden lg:flex lg:items-center lg:justify-center lg:px-8 py-12 ${isRTL ? 'order-1' : 'order-2'}`}>
          <div className="w-full max-w-md space-y-8">
            <div>
              <h2 className={`text-3xl font-bold text-white mb-2 ${isRTL ? 'text-right' : ''}`}>{t('Secure OTP Delivery')}</h2>
              <p className={`text-[#A0AEC0] ${isRTL ? 'text-right' : ''}`}>{t('Trusted by businesses worldwide')}</p>
            </div>

            {/* Features */}
            <div className="space-y-4">
              <div className={`flex gap-4 p-4 bg-[#1A1F2E]/50 border border-[#2D3748] rounded-2xl hover:border-[#128C7E]/40 transition-all ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className="w-12 h-12 bg-[#128C7E]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-6 h-6 text-[#128C7E]" />
                </div>
                <div className={isRTL ? 'text-right' : ''}>
                  <h3 className="text-white font-bold mb-1">{t('WhatsApp OTP')}</h3>
                  <p className="text-[#718096] text-sm">{t('Instant delivery via WhatsApp')}</p>
                </div>
              </div>

              <div className={`flex gap-4 p-4 bg-[#1A1F2E]/50 border border-[#2D3748] rounded-2xl hover:border-[#128C7E]/40 transition-all ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className="w-12 h-12 bg-[#128C7E]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Zap className="w-6 h-6 text-[#128C7E]" />
                </div>
                <div className={isRTL ? 'text-right' : ''}>
                  <h3 className="text-white font-bold mb-1">{t('Lightning Fast')}</h3>
                  <p className="text-[#718096] text-sm">{t('99.9% uptime guarantee')}</p>
                </div>
              </div>

              <div className={`flex gap-4 p-4 bg-[#1A1F2E]/50 border border-[#2D3748] rounded-2xl hover:border-[#128C7E]/40 transition-all ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className="w-12 h-12 bg-[#128C7E]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-[#128C7E]" />
                </div>
                <div className={isRTL ? 'text-right' : ''}>
                  <h3 className="text-white font-bold mb-1">{t('Enterprise Security')}</h3>
                  <p className="text-[#718096] text-sm">{t('Bank-level encryption')}</p>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mt-8 pt-8 border-t border-[#2D3748]">
              <div className={isRTL ? 'text-right' : 'text-center'}>
                <p className="text-2xl font-bold text-[#128C7E]">1M+</p>
                <p className="text-[#718096] text-sm mt-1">{t('OTPs Sent')}</p>
              </div>
              <div className={isRTL ? 'text-right' : 'text-center'}>
                <p className="text-2xl font-bold text-[#128C7E]">10+</p>
                <p className="text-[#718096] text-sm mt-1">{t('Customers')}</p>
              </div>
              <div className={isRTL ? 'text-right' : 'text-center'}>
                <p className="text-2xl font-bold text-[#128C7E]">99.9%</p>
                <p className="text-[#718096] text-sm mt-1">{t('Success Rate')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
